import {
  filterGroupsByTypes,
  findMeaningById,
  findWordById,
  groupWords,
  navigationForWord,
  searchGroups,
  type GroupedWord,
  type WordOpenTarget,
} from "../../domain/vocabulary/grouping.ts";
import {
  dueEligibleMeanings,
  extraEligibleMeanings,
  nextReviewTime,
  practiceStateFor,
  unavailableOverdueMeanings,
} from "../../domain/practice/engine.ts";
import { dueCardMeanings } from "../../domain/practice/cards.ts";
import { isDue } from "../../domain/practice/scheduling.ts";
import type { CardDirection } from "../../domain/vocabulary/types.ts";
import { isMeaningPracticeReady } from "../../domain/exercises/validate.ts";
import type { MeaningCard, VocabularySnapshot, WordType } from "../../domain/vocabulary/types.ts";
import type { VocabularyStore } from "../ports/storage.ts";

export interface HomeCounts {
  wordCount: number;
  meaningCount: number;
  writtenDueCount: number;
  cardsMeaningDueCount: number;
  cardsWordDueCount: number;
  unavailableOverdueCount: number;
}

export interface BrowseQuery {
  search: string;
  types: WordType[];
}

export class VocabularyQueries {
  constructor(private readonly store: VocabularyStore) {}

  snapshot(): VocabularySnapshot {
    const current = this.store.getSnapshot();
    if (!current) {
      throw new Error("Vocabulary has not been loaded.");
    }
    return current;
  }

  groups(query: BrowseQuery = { search: "", types: [] }): GroupedWord[] {
    return filterGroupsByTypes(searchGroups(groupWords(this.snapshot()), query.search), query.types);
  }

  recentGroups(limit = 5): GroupedWord[] {
    return groupWords(this.snapshot()).slice(0, limit);
  }

  counts(now: string): HomeCounts {
    const snapshot = this.snapshot();
    return {
      wordCount: snapshot.words.filter((word) =>
        snapshot.meanings.some((meaning) => meaning.wordId === word.id),
      ).length,
      meaningCount: snapshot.meanings.length,
      writtenDueCount: dueEligibleMeanings(snapshot, now).length,
      cardsMeaningDueCount: dueCardMeanings(snapshot, "hide_meaning", now).length,
      cardsWordDueCount: dueCardMeanings(snapshot, "hide_word", now).length,
      unavailableOverdueCount: unavailableOverdueMeanings(snapshot, now).length,
    };
  }

  extraPracticeCount(): number {
    return extraEligibleMeanings(this.snapshot()).length;
  }

  nextReviewAt(now: string): string | null {
    return nextReviewTime(this.snapshot(), now);
  }

  meaningScheduleLabel(meaningId: string, now: string): string {
    return this.scheduleLine(meaningId, now, "written");
  }

  scheduleLines(meaningId: string, now: string): { written: string; hideMeaning: string; hideWord: string } {
    return {
      written: this.scheduleLine(meaningId, now, "written"),
      hideMeaning: this.scheduleLine(meaningId, now, "hide_meaning"),
      hideWord: this.scheduleLine(meaningId, now, "hide_word"),
    };
  }

  private scheduleLine(meaningId: string, now: string, track: "written" | CardDirection): string {
    const snapshot = this.snapshot();
    const meaning = findMeaningById(snapshot, meaningId);
    const state = practiceStateFor(snapshot, meaningId);
    const label = track === "written" ? "Written" : track === "hide_meaning" ? "Cards: meaning" : "Cards: word";
    if (track === "written" && meaning && !isMeaningPracticeReady(snapshot, meaning)) {
      return `${label} · Stage ${state.stage} · Needs new exercises`;
    }
    const stage = track === "written" ? state.stage : state.cardSchedules[track].stage;
    const dueAt = track === "written" ? state.dueAt : state.cardSchedules[track].dueAt;
    if (isDue(dueAt, now)) {
      return `${label} · Stage ${stage} · Due now`;
    }
    return `${label} · Stage ${stage} · Next ${formatLocalDate(dueAt)}`;
  }

  stageLabel(meaningId: string): string {
    return `Stage ${practiceStateFor(this.snapshot(), meaningId).stage}`;
  }

  openWord(wordId: string): WordOpenTarget | { kind: "missing" } {
    const snapshot = this.snapshot();
    const word = findWordById(snapshot, wordId);
    if (!word) {
      return { kind: "missing" };
    }
    const meanings = snapshot.meanings.filter((meaning) => meaning.wordId === wordId);
    return navigationForWord(meanings) ?? { kind: "missing" };
  }

  meaning(meaningId: string): MeaningCard | undefined {
    return findMeaningById(this.snapshot(), meaningId);
  }
}

export function formatLocalDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString(undefined, { dateStyle: "medium" });
}

export function formatLocalDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
